import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, History, Camera, Mail, Download } from "lucide-react";
import AppShell, { useAppUser, ROLE_LABELS } from "@/components/AppShell";
import BeltBar, { BELTS, BELT_LABELS, maxDegree } from "@/components/BeltBar";
import UserAvatar from "@/components/UserAvatar";
import CameraCapture from "@/components/CameraCapture";
import { authApi, formatApiError } from "@/lib/api";
import { imageToDataUrl } from "@/lib/files";

const inputCls =
  "w-full bg-obsidian border border-white/10 focus:border-gold/60 rounded-xl px-4 py-3 text-sm text-[#F5F5F0] placeholder:text-zinc-600 outline-none transition-colors duration-200";

const EMPTY_FORM = {
  name: "", email: "", password: "", roles: ["aluno"], belt: "branca", degree: 0,
  birth_date: "", phone: "", rg: "", cpf: "", address: "", institution: "", weight: "", height: "", unit_id: "", photo: "", censura: false,
  guardian_name: "", guardian_cpf: "", guardian_rg: "", guardian_email: "", guardian_relationship: "",
};

const ageOf = (bd) => {
  if (!bd) return null;
  const d = new Date(bd + "T00:00:00");
  if (isNaN(d)) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--;
  return age;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

export default function AdminUsers() {
  const { user } = useAppUser() || {};
  const isAdmin = (user?.roles || [user?.role]).includes("admin");
  const [users, setUsers] = useState(null);
  const [units, setUnits] = useState([]);
  const [filterRoles, setFilterRoles] = useState([]);
  const [userForm, setUserForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [gradHistory, setGradHistory] = useState({});
  const [camOpen, setCamOpen] = useState(false);
  const location = useLocation();
  const photoRef = useRef(null);

  useEffect(() => {
    const novo = !!new URLSearchParams(location.search).get("novo");
    setEditingEmail(null);
    if (novo) setUserForm(EMPTY_FORM);
    setFormOpen(novo);
  }, [location.search]);

  const refreshUsers = async () => {
    const res = await authApi().get("/users");
    setUsers(res.data.users);
  };

  useEffect(() => {
    if (!user) return;
    refreshUsers();
    authApi()
      .get("/units")
      .then((res) => setUnits(res.data.units || []))
      .catch(() => {});
  }, [user]);

  const unitName = (id) => (units.find((u) => u.id === id) || {}).name || null;

  const toggleRole = (r) => {
    setUserForm((f) => {
      const next = f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r];
      return { ...f, roles: next.length ? next : f.roles };
    });
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const dataUrl = await imageToDataUrl(file);
      setUserForm((f) => ({ ...f, photo: dataUrl }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const buildPayload = (force = false) => ({
    name: userForm.name,
    roles: userForm.roles,
    belt: userForm.belt,
    degree: userForm.degree,
    birth_date: userForm.birth_date || null,
    phone: userForm.phone,
    rg: userForm.rg,
    cpf: userForm.cpf,
    address: userForm.address,
    institution: userForm.institution,
    weight: userForm.weight ? Number(userForm.weight) : null,
    height: userForm.height ? Number(userForm.height) : null,
    unit_id: userForm.unit_id,
    photo: userForm.photo,
    censura: userForm.censura,
    guardian_name: userForm.guardian_name,
    guardian_cpf: userForm.guardian_cpf,
    guardian_rg: userForm.guardian_rg,
    guardian_email: userForm.guardian_email,
    guardian_relationship: userForm.guardian_relationship,
    force,
  });

  const submitUser = async (e, force = false) => {
    if (e?.preventDefault) e.preventDefault();
    setCreating(true);
    try {
      if (editingEmail) {
        const payload = buildPayload(force);
        if (userForm.password) payload.password = userForm.password;
        await authApi().patch(`/users/${encodeURIComponent(editingEmail)}`, payload);
        toast.success(`${userForm.name} atualizado.`);
        setEditingEmail(null);
      } else {
        const res = await authApi().post("/users", { ...buildPayload(), email: userForm.email, password: userForm.password });
        toast.success(`${userForm.name} cadastrado: ${userForm.roles.map((r) => ROLE_LABELS[r]).join(" + ")}.`);
        if (res.data?.termo_enviado) toast.success("Termo de responsabilidade enviado ao e-mail do responsável legal.");
      }
      setUserForm(EMPTY_FORM);
      setFormOpen(false);
      await refreshUsers();
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(`${userForm.name} está com pendência de boletim. Deseja forçar a graduação mesmo assim?`)) {
          return submitUser(null, true);
        }
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (u) => {
    setEditingEmail(u.email);
    setUserForm({
      name: u.name,
      email: u.email,
      password: "",
      roles: u.roles || [u.role || "aluno"],
      belt: u.belt || "branca",
      degree: u.degree || 0,
      birth_date: u.birth_date || "",
      phone: u.phone || "",
      rg: u.rg || "",
      cpf: u.cpf || "",
      address: u.address || "",
      institution: u.institution || "",
      weight: u.weight ?? "",
      height: u.height ?? "",
      unit_id: u.unit_id || "",
      photo: u.photo || "",
      censura: !!u.censura,
      guardian_name: u.guardian_name || "",
      guardian_cpf: u.guardian_cpf || "",
      guardian_rg: u.guardian_rg || "",
      guardian_email: u.guardian_email || "",
      guardian_relationship: u.guardian_relationship || "",
    });
    setFormOpen(true);
    document.querySelector('[data-testid="admin-create-user-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const cancelEdit = () => {
    setEditingEmail(null);
    setUserForm(EMPTY_FORM);
    setFormOpen(false);
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Excluir o usuário ${u.name} (${u.email})?`)) return;
    try {
      await authApi().delete(`/users/${encodeURIComponent(u.email)}`);
      toast.success(`${u.name} removido.`);
      if (editingEmail === u.email) cancelEdit();
      await refreshUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const resendTermo = async (u) => {
    try {
      await authApi().post(`/users/${encodeURIComponent(u.email)}/termo/reenviar`);
      toast.success(`Termo reenviado para ${u.guardian_email || "o responsável legal"}.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const downloadTermo = async (u) => {
    try {
      const res = await authApi().get(`/users/${encodeURIComponent(u.email)}/termo/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `termo_responsabilidade_${u.email.split("@")[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Termo assinado não encontrado.");
    }
  };

  const removerAusencia = async (u) => {
    try {
      await authApi().post(`/users/${encodeURIComponent(u.email)}/ausencia/remover`);
      toast.success(`Pendência de ausência removida de ${u.name}.`);
      await refreshUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleHistory = async (u) => {
    const key = u.email;
    if (gradHistory[key]?.open) {
      setGradHistory((g) => ({ ...g, [key]: { ...g[key], open: false } }));
      return;
    }
    try {
      const res = await authApi().get(`/users/${encodeURIComponent(key)}/graduations`);
      setGradHistory((g) => ({ ...g, [key]: { open: true, items: res.data.graduations } }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateGraduation = async (u, belt, degree, force = false) => {
    try {
      await authApi().patch("/users/graduation", { email: u.email, belt, degree, force });
      await refreshUsers();
      toast.success(force ? "Graduação forçada e registrada." : "Graduação atualizada.");
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(`${u.name} está com pendência de boletim. Deseja forçar a graduação mesmo assim?`)) {
          return updateGraduation(u, belt, degree, true);
        }
        await refreshUsers();
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
        await refreshUsers();
      }
    }
  };

  const userRoles = (u) => u.roles || [u.role || "aluno"];
  const toggleFilter = (r) =>
    setFilterRoles((f) => (f.includes(r) ? f.filter((x) => x !== r) : [...f, r]));
  const filtered = (users || []).filter(
    (u) => filterRoles.length === 0 || filterRoles.every((r) => userRoles(u).includes(r))
  );
  const formAge = ageOf(userForm.birth_date);
  const formIsMinor = formAge !== null && formAge < 18;

  return (
    <AppShell active="usuarios" requireRoles={["admin", "responsavel"]}>
      <section data-testid="admin-users-section">
        <h1 className="font-display text-2xl font-bold" data-testid="admin-users-title">Usuários do App</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-6" data-testid="admin-users-label">
          GERENCIAMENTO {users ? `(${users.length})` : ""}
        </p>

        {formOpen && (
        <form onSubmit={(e) => submitUser(e)} className="bg-surface border border-white/10 rounded-2xl p-6 grid sm:grid-cols-2 gap-4" data-testid="admin-create-user-form">
          {editingEmail && (
            <p className="sm:col-span-2 font-mono text-xs tracking-widest text-gold" data-testid="user-form-editing-label">
              EDITANDO: {editingEmail}
            </p>
          )}

          <div className="sm:col-span-2 flex items-center gap-4 flex-wrap">
            <UserAvatar name={userForm.name || "?"} photo={userForm.photo || null} size="md" testid="user-form-photo-preview" />
            <button type="button" onClick={() => photoRef.current?.click()} data-testid="user-photo-button" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest border border-white/15 text-zinc-300 px-4 py-2.5 rounded-full hover:border-gold/50 hover:text-gold transition-colors">
              <Camera size={14} /> {userForm.photo ? "TROCAR FOTO" : "ADICIONAR FOTO"}
            </button>
            <button type="button" onClick={() => setCamOpen(true)} data-testid="user-photo-camera-button" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest border border-gold/40 text-gold px-4 py-2.5 rounded-full hover:bg-gold hover:text-obsidian transition-colors">
              <Camera size={14} /> ABRIR CÂMERA
            </button>
            {userForm.photo && (
              <button type="button" onClick={() => setUserForm((f) => ({ ...f, photo: "" }))} data-testid="user-photo-remove" className="font-mono text-[11px] tracking-widest text-zinc-500 hover:text-crimson transition-colors">
                REMOVER
              </button>
            )}
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={pickPhoto} data-testid="user-photo-input" />
          </div>
          {camOpen && (
            <CameraCapture
              testid="user-camera"
              onCapture={(dataUrl) => {
                setUserForm((f) => ({ ...f, photo: dataUrl }));
                setCamOpen(false);
              }}
              onClose={() => setCamOpen(false)}
            />
          )}

          <input required minLength={2} value={userForm.name} onChange={(e) => setUserForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nome completo" className={inputCls} data-testid="user-name-input" />
          <input required type="email" disabled={!!editingEmail} value={userForm.email} onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))} placeholder="E-mail" className={`${inputCls} disabled:opacity-50`} data-testid="user-email-input" />
          <div>
            <input required type="date" value={userForm.birth_date} onChange={(e) => {
              const bd = e.target.value;
              const minor = ageOf(bd) !== null && ageOf(bd) < 18;
              setUserForm((f) => ({ ...f, birth_date: bd, censura: minor ? true : f.censura }));
            }} className={`${inputCls} [color-scheme:dark]`} data-testid="user-birthdate-input" title="Data de nascimento" />
            {formAge !== null && <p className="font-mono text-[11px] text-gold mt-1.5" data-testid="user-age-preview">IDADE: {formAge} ANOS</p>}
          </div>
          <input required={!editingEmail} type="password" minLength={6} value={userForm.password} onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))} placeholder={editingEmail ? "Nova senha (vazio mantém a atual)" : "Senha temporária (mín. 6)"} className={inputCls} data-testid="user-password-input" />
          <input value={userForm.phone} onChange={(e) => setUserForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Celular (ex: 15 99759-2285)" className={inputCls} data-testid="user-phone-input" />
          <input value={userForm.rg} onChange={(e) => setUserForm((f) => ({ ...f, rg: e.target.value }))} placeholder="RG (opcional)" className={inputCls} data-testid="user-rg-input" />
          <input value={userForm.cpf} onChange={(e) => setUserForm((f) => ({ ...f, cpf: e.target.value }))} placeholder="CPF (opcional)" className={inputCls} data-testid="user-cpf-input" />
          <input value={userForm.address} onChange={(e) => setUserForm((f) => ({ ...f, address: e.target.value }))} placeholder="Endereço" className={inputCls} data-testid="user-address-input" />
          <input value={userForm.institution} onChange={(e) => setUserForm((f) => ({ ...f, institution: e.target.value }))} placeholder="Instituição de acolhimento (opcional)" className={inputCls} data-testid="user-institution-input" />
          <select value={userForm.unit_id} onChange={(e) => setUserForm((f) => ({ ...f, unit_id: e.target.value }))} className={inputCls} data-testid="user-unit-select">
            <option value="">Sem unidade vinculada</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input type="number" min={0} step="0.1" value={userForm.weight} onChange={(e) => setUserForm((f) => ({ ...f, weight: e.target.value }))} placeholder="Peso (kg)" className={inputCls} data-testid="user-weight-input" />
            <input type="number" min={0} step="0.5" value={userForm.height} onChange={(e) => setUserForm((f) => ({ ...f, height: e.target.value }))} placeholder="Altura (cm)" className={inputCls} data-testid="user-height-input" />
          </div>

          <div className="sm:col-span-2 flex items-center gap-3 flex-wrap border border-white/10 rounded-xl px-4 py-3" data-testid="user-censura-row">
            <button
              type="button"
              onClick={() => setUserForm((f) => ({ ...f, censura: !f.censura }))}
              data-testid="user-censura-toggle"
              aria-pressed={userForm.censura}
              className={`font-mono text-[11px] tracking-wider px-4 py-2 rounded-full border transition-colors ${userForm.censura ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-500 hover:border-gold/40"}`}
            >
              CENSURA DE IMAGENS: {userForm.censura ? "SIM" : "NÃO"}
            </button>
            <p className="text-[11px] text-zinc-500 flex-1 min-w-[200px]">
              {formIsMinor
                ? "Menor de 18 anos: marcado SIM automaticamente. Você pode mudar para NÃO para permitir a exibição (ex.: com autorização do responsável)."
                : "Se SIM, o rosto deste usuário será borrado quando identificado nas fotos da Galeria."}
            </p>
          </div>

          {formIsMinor && userForm.roles.includes("aluno") && (
            <div className="sm:col-span-2 border border-crimson/40 bg-crimson/[0.04] rounded-xl p-4" data-testid="guardian-section">
              <p className="font-mono text-[10px] tracking-widest text-crimson mb-3">RESPONSÁVEL LEGAL — OBRIGATÓRIO (ALUNO MENOR DE IDADE)</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <input required minLength={2} value={userForm.guardian_name} onChange={(e) => setUserForm((f) => ({ ...f, guardian_name: e.target.value }))} placeholder="Nome do responsável legal" className={inputCls} data-testid="guardian-name-input" />
                <select required value={userForm.guardian_relationship} onChange={(e) => setUserForm((f) => ({ ...f, guardian_relationship: e.target.value }))} className={inputCls} data-testid="guardian-relationship-select">
                  <option value="">Relacionamento com o aluno</option>
                  <option value="pai">Pai</option>
                  <option value="mae">Mãe</option>
                  <option value="responsavel_legal">Responsável legal</option>
                </select>
                <input required value={userForm.guardian_cpf} onChange={(e) => setUserForm((f) => ({ ...f, guardian_cpf: e.target.value }))} placeholder="CPF do responsável" className={inputCls} data-testid="guardian-cpf-input" />
                <input required value={userForm.guardian_rg} onChange={(e) => setUserForm((f) => ({ ...f, guardian_rg: e.target.value }))} placeholder="RG do responsável" className={inputCls} data-testid="guardian-rg-input" />
                <input required type="email" value={userForm.guardian_email} onChange={(e) => setUserForm((f) => ({ ...f, guardian_email: e.target.value }))} placeholder="E-mail do responsável (receberá o termo)" className={`${inputCls} sm:col-span-2`} data-testid="guardian-email-input" />
              </div>
              <p className="text-[11px] text-zinc-500 mt-3">
                Ao salvar, o responsável recebe por e-mail o Termo de Responsabilidade para autorização digital. Enquanto não assinar, o aluno fica com pendência vermelha no cadastro.
              </p>
            </div>
          )}

          <div className="sm:col-span-2 flex flex-wrap gap-2 items-center" data-testid="user-roles-group">
            {Object.entries(ROLE_LABELS).filter(([r]) => isAdmin || r !== "admin").map(([r, label]) => (
              <button
                key={r}
                type="button"
                onClick={() => toggleRole(r)}
                data-testid={`user-role-${r}`}
                className={`font-mono text-[11px] tracking-wider px-3 py-2 rounded-full border transition-colors ${userForm.roles.includes(r) ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-500 hover:border-gold/40 hover:text-zinc-300"}`}
              >
                {label.toUpperCase()}
              </button>
            ))}
          </div>
          <select value={userForm.belt} onChange={(e) => setUserForm((f) => ({ ...f, belt: e.target.value, degree: Math.min(f.degree, maxDegree(e.target.value)) }))} className={inputCls} data-testid="user-belt-select">
            {BELTS.map((b) => (
              <option key={b} value={b}>Faixa {BELT_LABELS[b]}</option>
            ))}
          </select>
          <select value={userForm.degree} onChange={(e) => setUserForm((f) => ({ ...f, degree: Number(e.target.value) }))} className={inputCls} data-testid="user-degree-select">
            {Array.from({ length: maxDegree(userForm.belt) + 1 }).map((_, d) => (
              <option key={d} value={d}>{d === 0 ? "Sem grau" : `${d} ${d === 1 ? "grau" : "graus"}`}</option>
            ))}
          </select>
          <button type="submit" disabled={creating} data-testid="user-create-submit-button" className="sm:col-span-2 inline-flex items-center justify-center gap-3 font-display font-semibold bg-gold text-obsidian px-8 py-3.5 rounded-full hover:bg-gold-amber disabled:opacity-60 transition-all duration-300">
            <UserPlus size={18} /> {creating ? "Salvando..." : editingEmail ? "Salvar Alterações" : "Cadastrar Usuário"}
          </button>
          <button type="button" onClick={cancelEdit} data-testid="user-edit-cancel-button" className="sm:col-span-2 font-mono text-xs tracking-widest border border-white/15 text-zinc-400 px-8 py-3 rounded-full hover:border-crimson/60 hover:text-crimson transition-colors">
            {editingEmail ? "CANCELAR EDIÇÃO" : "FECHAR FORMULÁRIO"}
          </button>
        </form>
        )}

        <div className="mt-6" data-testid="admin-users-filters">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setFilterRoles([])}
              data-testid="filter-role-todos"
              className={`font-mono text-[11px] tracking-wider px-3.5 py-2 rounded-full border transition-colors ${filterRoles.length === 0 ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-500 hover:border-gold/40 hover:text-zinc-300"}`}
            >
              TODOS ({(users || []).length})
            </button>
            {Object.entries(ROLE_LABELS).map(([r, label]) => (
              <button
                key={r}
                type="button"
                onClick={() => toggleFilter(r)}
                data-testid={`filter-role-${r}`}
                aria-pressed={filterRoles.includes(r)}
                className={`font-mono text-[11px] tracking-wider px-3.5 py-2 rounded-full border transition-colors ${filterRoles.includes(r) ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-500 hover:border-gold/40 hover:text-zinc-300"}`}
              >
                {label.toUpperCase()} ({(users || []).filter((u) => userRoles(u).includes(r)).length})
              </button>
            ))}
          </div>
          {filterRoles.length > 1 && (
            <p className="font-mono text-[11px] text-gold/80 mt-2.5" data-testid="filter-combined-note">
              MOSTRANDO USUÁRIOS COM {filterRoles.map((r) => (ROLE_LABELS[r] || r).toUpperCase()).join(" + ")}
            </p>
          )}
        </div>

        <div className="mt-6 space-y-3" data-testid="admin-users-list">
          {filtered.map((u, i) => (
            <div key={u.id || u.email} className="bg-[#1C1C23] border border-white/10 rounded-xl px-5 py-4" data-testid={`admin-user-row-${i}`}>
              <div className="flex items-start gap-4">
                <UserAvatar name={u.name} photo={u.photo} pendencia={!!u.pendencia} termo={!!u.termo_pendente} birthDate={u.birth_date} size="md" testid={`admin-user-avatar-${i}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-semibold">{u.name}</p>
                    {u.pendencia && (
                      <span className="font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border border-yellow-400/50 text-yellow-300 bg-yellow-400/10" data-testid={`admin-user-pendencia-${i}`}>
                        PENDÊNCIA: BOLETIM
                      </span>
                    )}
                    {u.termo_pendente && (
                      <span className="font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border border-red-500/60 text-red-400 bg-red-500/10" data-testid={`admin-user-termo-${i}`}>
                        PENDÊNCIA: TERMO DO RESPONSÁVEL
                      </span>
                    )}
                    {u.termo_pendente && u.guardian_email && (
                      <button type="button" onClick={() => resendTermo(u)} data-testid={`admin-user-termo-resend-${i}`}
                        className="font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border border-gold/40 text-gold hover:bg-gold/15 transition-colors inline-flex items-center gap-1.5">
                        <Mail size={11} /> REENVIAR TERMO
                      </button>
                    )}
                    {u.termo_assinado && (
                      <button type="button" onClick={() => downloadTermo(u)} data-testid={`admin-user-termo-download-${i}`}
                        className="font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border border-emerald-500/50 text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors inline-flex items-center gap-1.5">
                        <Download size={11} /> TERMO ASSINADO
                      </button>
                    )}
                    {u.ausencia_pendente && (
                      <>
                        <span className="font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border border-orange-500/60 text-orange-400 bg-orange-500/10" data-testid={`admin-user-ausencia-${i}`}>
                          AUSÊNCIA FREQUENTE ({u.faltas_seguidas || 0} FALTAS)
                        </span>
                        <button type="button" onClick={() => removerAusencia(u)} data-testid={`admin-user-ausencia-remover-${i}`}
                          className="font-mono text-[10px] tracking-wider px-2.5 py-1 rounded-full border border-gold/40 text-gold hover:bg-gold/15 transition-colors inline-flex items-center gap-1.5">
                          REMOVER
                        </button>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-0.5 truncate" data-testid={`admin-user-meta-${i}`}>
                    {u.email}
                    {u.age != null && <span className="text-zinc-600"> · {u.age} anos</span>}
                    {u.phone && <span className="text-zinc-600"> · {u.phone}</span>}
                    {u.cpf && <span className="text-zinc-600"> · CPF {u.cpf}</span>}
                    {u.rg && <span className="text-zinc-600"> · RG {u.rg}</span>}
                    {unitName(u.unit_id) && <span className="text-zinc-600"> · {unitName(u.unit_id)}</span>}
                    {u.censura && <span className="text-yellow-500/80"> · CENSURA DE IMAGEM ATIVA</span>}
                  </p>
                  <p className="font-mono text-[11px] tracking-wider text-gold/80 mt-1" data-testid={`admin-user-aulas-${i}`}>
                    {u.aulas || 0} {(u.aulas || 0) === 1 ? "AULA FEITA" : "AULAS FEITAS"}
                  </p>
                  <div className="mt-2.5 flex items-center gap-2 flex-wrap min-h-[26px]" data-testid={`admin-user-roles-${i}`}>
                    {(u.roles || [u.role || "aluno"]).map((r) => (
                      <span key={r} className={`font-mono text-[11px] tracking-wider px-3 py-1.5 rounded-full border ${r === "admin" ? "border-gold/50 text-gold bg-gold/10" : "border-white/15 text-zinc-400"}`} data-testid={`admin-user-role-${i}-${r}`}>
                        {(ROLE_LABELS[r] || r).toUpperCase()}
                      </span>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <BeltBar belt={u.belt || "branca"} degree={u.degree || 0} size="sm" testid={`admin-user-beltbar-${i}`} />
                    <select
                      value={u.belt || "branca"}
                      onChange={(e) => updateGraduation(u, e.target.value, 0)}
                      className="bg-obsidian border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-300 outline-none focus:border-gold/50"
                      data-testid={`admin-user-belt-${i}`}
                    >
                      {BELTS.map((b) => (
                        <option key={b} value={b}>Faixa {BELT_LABELS[b]}</option>
                      ))}
                    </select>
                    <select
                      value={u.degree || 0}
                      onChange={(e) => updateGraduation(u, u.belt || "branca", Number(e.target.value))}
                      className="bg-obsidian border border-white/10 rounded-lg px-2 py-1.5 text-xs text-zinc-300 outline-none focus:border-gold/50"
                      data-testid={`admin-user-degree-${i}`}
                    >
                      {Array.from({ length: maxDegree(u.belt || "branca") + 1 }).map((_, d) => (
                        <option key={d} value={d}>{d === 0 ? "Sem grau" : `${d}º grau`}</option>
                      ))}
                    </select>
                    <span className="font-mono text-[10px] text-zinc-600" data-testid={`admin-user-last-grad-${i}`}>
                      ÚLT. GRADUAÇÃO: {fmtDate(u.last_graduation_at)}
                    </span>
                    <span
                      className={`font-mono text-[10px] ${(u.aulas_desde_grad || 0) >= 20 ? "text-green-400" : "text-yellow-400"}`}
                      data-testid={`admin-user-aulas-grad-${i}`}
                      title="Aulas desde a última graduação (mínimo de 20 para graduar)"
                    >
                      {u.aulas_desde_grad || 0}/20 AULAS P/ GRADUAR
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button type="button" onClick={() => toggleHistory(u)} data-testid={`admin-user-history-button-${i}`} className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors ${gradHistory[u.email]?.open ? "border-gold/60 text-gold bg-gold/10" : "border-white/15 text-zinc-400 hover:text-gold hover:border-gold/50"}`} aria-label={`Histórico de ${u.name}`}>
                    <History size={14} />
                  </button>
                  {(isAdmin || !userRoles(u).includes("admin")) && (
                    <button type="button" onClick={() => startEdit(u)} data-testid={`admin-user-edit-${i}`} className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center text-zinc-400 hover:text-gold hover:border-gold/50 transition-colors" aria-label={`Editar ${u.name}`}>
                      <Pencil size={14} />
                    </button>
                  )}
                  {isAdmin && (
                    <button type="button" onClick={() => deleteUser(u)} data-testid={`admin-user-delete-${i}`} className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center text-zinc-400 hover:text-crimson hover:border-crimson/60 transition-colors" aria-label={`Excluir ${u.name}`}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              {gradHistory[u.email]?.open && (
                <div className="mt-4 pt-4 border-t border-white/10 space-y-2.5" data-testid={`admin-user-history-${i}`}>
                  {(gradHistory[u.email].items || []).length === 0 ? (
                    <p className="text-xs text-zinc-500">Nenhuma graduação registrada.</p>
                  ) : (
                    gradHistory[u.email].items.map((g, j) => (
                      <div key={g.id} className="flex items-center gap-3 flex-wrap" data-testid={`admin-user-history-item-${i}-${j}`}>
                        <BeltBar belt={g.belt} degree={g.degree} size="sm" />
                        <span className="text-xs text-zinc-300">Faixa {BELT_LABELS[g.belt]} · {g.degree === 0 ? "sem grau" : `${g.degree} ${g.degree === 1 ? "grau" : "graus"}`}</span>
                        <span className="font-mono text-[11px] text-zinc-600">{new Date(g.created_at).toLocaleDateString("pt-BR")} · por {g.granted_by}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
          {users !== null && filtered.length === 0 && (
            <p className="text-sm text-zinc-500 border border-dashed border-white/15 rounded-xl py-10 text-center" data-testid="admin-users-filter-empty">
              Nenhum usuário com esse perfil.
            </p>
          )}
        </div>
      </section>
    </AppShell>
  );
}
